import React, {useRef} from "react";
import {MinusSmIcon, PlusSmIcon} from "@heroicons/react/solid";

let nextId = 0;

/**
 * Accessible "- 0 +" stepper used for picking a number of travelers.
 *
 * The buttons do the work for mouse/touch users. The value itself is a
 * focusable spinbutton so keyboard users can tab to it and use the arrow
 * keys, and screen readers get the count announced through a live region.
 *
 * @param {number} value             Current count
 * @param {string} label             Human readable name, e.g. "Vuxen 18+"
 * @param {string} [labelledBy]      Ids of visible elements naming the control
 * @param {number} [min=0]           Lowest allowed value
 * @param {number} [max]             Highest allowed value, if there is one
 * @param {Function} onIncrement
 * @param {Function} onDecrement
 */
export default function NumberStepper({
    value,
    label,
    labelledBy,
    min = 0,
    max,
    onIncrement,
    onDecrement,
}) {
    const idRef = useRef(null);
    if (idRef.current === null) {
        idRef.current = `stepper-${++nextId}`;
    }
    const liveId = `${idRef.current}-live`;

    const canDecrement = value > min;
    const canIncrement = max == null || value < max;

    const decrement = () => {
        if (canDecrement) {
            onDecrement();
        }
    };

    const increment = () => {
        if (canIncrement) {
            onIncrement();
        }
    };

    const handleKeyDown = (event) => {
        switch (event.key) {
            case "ArrowUp":
            case "ArrowRight":
            case "PageUp":
                event.preventDefault();
                increment();
                break;
            case "ArrowDown":
            case "ArrowLeft":
            case "PageDown":
                event.preventDefault();
                decrement();
                break;
            default:
                break;
        }
    };

    return (
        <div
            role="group"
            aria-labelledby={labelledBy}
            aria-label={labelledBy ? undefined : label}
            className="stepper inline-flex h-10 items-stretch rounded-md border border-gray-300 bg-white shadow-sm overflow-hidden"
        >
            <button
                type="button"
                onClick={decrement}
                aria-disabled={!canDecrement}
                aria-label={`Ta bort ${label}`}
                aria-describedby={liveId}
                className="stepper__button w-10 flex items-center justify-center text-gray-600"
            >
                <MinusSmIcon className="h-5 w-5" aria-hidden="true" />
            </button>
            <div
                role="spinbutton"
                tabIndex={0}
                aria-labelledby={labelledBy}
                aria-label={labelledBy ? undefined : label}
                aria-valuenow={value}
                aria-valuemin={min}
                aria-valuemax={max == null ? undefined : max}
                aria-valuetext={`${value} ${label}`}
                onKeyDown={handleKeyDown}
                className="stepper__value w-12 flex items-center justify-center border-l border-r border-gray-300 text-base font-semibold text-gray-900 tabular-nums select-none"
            >
                {value}
            </div>
            <button
                type="button"
                onClick={increment}
                aria-disabled={!canIncrement}
                aria-label={`Lägg till ${label}`}
                aria-describedby={liveId}
                className="stepper__button w-10 flex items-center justify-center text-gray-600"
            >
                <PlusSmIcon className="h-5 w-5" aria-hidden="true" />
            </button>
            <span id={liveId} className="sr-only" aria-live="polite" aria-atomic="true">
                {value} {label}
            </span>
        </div>
    );
}
